import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react';
import Shell from './components/layout/Shell.js';
import Dashboard from './pages/Dashboard.js';
import Companies from './pages/Companies.js';
import CompanyDetail from './pages/CompanyDetail.js';
import Contacts from './pages/Contacts.js';
import ContactDetail from './pages/ContactDetail.js';
import Buckets from './pages/Buckets.js';
import BucketDetail from './pages/BucketDetail.js';
import Orders from './pages/Orders.js';
import OrderDetail from './pages/OrderDetail.js';
import Kanban from './pages/Kanban.js';
import Settings, { SettingsIndex } from './pages/Settings.js';
import StagesSettings from './pages/settings/StagesSettings.js';
import CustomFieldsSettings from './pages/settings/CustomFieldsSettings.js';
import ValidationRulesSettings from './pages/settings/ValidationRulesSettings.js';
import WorkspaceSettings from './pages/settings/WorkspaceSettings.js';
import AppearanceSettings from './pages/settings/AppearanceSettings.js';
import MembersSettings from './pages/settings/MembersSettings.js';
import NotificationsSettings from './pages/settings/NotificationsSettings.js';
import SecuritySettings from './pages/settings/SecuritySettings.js';
import MfaSettings from './pages/settings/MfaSettings.js';
import FocusedView from './pages/FocusedView.js';
import JoinWorkspace from './pages/JoinWorkspace.js';
import Reports from './pages/Reports.js';
import Onboarding from './pages/Onboarding.js';
import NotFound from './pages/NotFound.js';
import Toaster from './components/ui/Toaster.js';
import { useWorkspaceStore } from './stores/workspace.js';
import { api } from './lib/api.js';
import { initPostHog } from './lib/posthog.js';
import type { User, Workspace } from '@mayksel/types';

function WorkspaceLoader({ children }: { children: React.ReactNode }) {
  const { getToken, userId } = useAuth();
  const { setWorkspaceData, clearWorkspace } = useWorkspaceStore();

  useEffect(() => {
    if (!userId) return;

    void (async () => {
      try {
        const token = await getToken();
        const opts: { token?: string | undefined } = {};
        if (token) opts.token = token;
        const result = await api.get<{ user: User | null; workspace: Workspace | null }>(
          '/v1/me',
          opts,
        );

        if (result.workspace && result.user) {
          setWorkspaceData(result.workspace, result.user);
          initPostHog(result.user.id);
        } else {
          clearWorkspace();
        }
      } catch (_err) {
        clearWorkspace();
      }
    })();
  // Only run when the Clerk userId changes — getToken/setWorkspaceData/clearWorkspace are stable refs
  }, [userId]);

  return <>{children}</>;
}

function AppRoutes() {
  const { workspace, isLoading } = useWorkspaceStore();

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="skeleton-block" style={{ width: 32, height: 32, borderRadius: 'var(--radius-full)' }} />
      </div>
    );
  }

  // No workspace yet → onboarding
  if (!workspace) {
    return <Onboarding />;
  }

  // Onboarding not complete → resume onboarding
  if (!workspace.onboardingComplete) {
    return <Onboarding />;
  }

  return (
    <Shell>
      <Routes>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="companies" element={<Companies />} />
        <Route path="companies/:id" element={<CompanyDetail />} />
        <Route path="contacts" element={<Contacts />} />
        <Route path="contacts/:id" element={<ContactDetail />} />
        <Route path="projects" element={<Buckets />} />
        <Route path="projects/:id" element={<BucketDetail />} />
        <Route path="orders" element={<Orders />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="board" element={<Kanban />} />
        <Route path="reports" element={<Reports />} />
        <Route path="settings" element={<Settings />}>
          <Route index element={<SettingsIndex />} />
          <Route path="stages" element={<StagesSettings />} />
          <Route path="custom-fields" element={<CustomFieldsSettings />} />
          <Route path="validation-rules" element={<ValidationRulesSettings />} />
          <Route path="workspace" element={<WorkspaceSettings />} />
          <Route path="appearance" element={<AppearanceSettings />} />
          <Route path="members" element={<MembersSettings />} />
          <Route path="notifications" element={<NotificationsSettings />} />
          <Route path="security" element={<SecuritySettings />} />
          <Route path="mfa" element={<MfaSettings />} />
        </Route>
        <Route path="focus/view/:viewId" element={<FocusedView />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/join"
          element={
            <>
              <SignedIn>
                <JoinWorkspace />
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
        <Route
          path="/*"
          element={
            <>
              <SignedIn>
                <WorkspaceLoader>
                  <AppRoutes />
                </WorkspaceLoader>
              </SignedIn>
              <SignedOut>
                <RedirectToSignIn />
              </SignedOut>
            </>
          }
        />
      </Routes>
      <Toaster />
    </BrowserRouter>
  );
}
